import React from 'react';
import Head from 'next/head';
import Script from 'next/script';

import { IBlogPostFields, IPageFields } from '../../../types/contentful';

import { usePageDataContext } from '../../context/PageData';
import { usePublicRuntimeConfig } from '../../hooks/usePublicRuntimeConfig';

import FAQRichResults from './FAQRichResults';
import { DynamicSchemaJson } from '../BlogPost/DynamicSchemaJson';

import glIcon from '@/images/gl-icon.png';

type PageFields = Partial<IPageFields> & {
  heroImage?: IBlogPostFields['heroImage'];
  publishDate?: IBlogPostFields['publishDate'];
};

interface MetadataProps extends PageFields {
  pathPrefix?: string;
}

export function Metadata({
  seoMetadata,
  slug,
  schema,
  heroImage,
  publishDate,
  queryString,
  pathPrefix
}: MetadataProps) {
  const { siteMetadata } = usePublicRuntimeConfig();

  const { config: { seoMetadata: globalSeoMetadata } = {} } =
    usePageDataContext();

  const { seoTitle, seoMetaDescription, seoImage, isNoIndexed } =
    seoMetadata?.fields ?? {};

  const title = seoTitle ?? globalSeoMetadata?.fields.seoTitle ?? '';
  const description =
    seoMetaDescription ?? globalSeoMetadata?.fields?.seoMetaDescription ?? '';
  const seoImageUrl =
    seoImage?.fields?.file?.url ??
    globalSeoMetadata?.fields?.seoImage?.fields?.file?.url ??
    siteMetadata?.seoImageUrl;

  const path = slug === 'homepage' ? '' : `/${slug}/`;
  const url = `https://greenlight.com${pathPrefix}${path}`;

  const image = heroImage ? `https:${heroImage?.fields?.file?.url}` : '';
  const datePublished = publishDate
    ? new Date(publishDate).toISOString().split('T')[0]
    : '';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta
          name="facebook-domain-verification"
          content="a9ko4grvdv4htmv12dsq1r8614p46q"
        />
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <link rel="icon" href={glIcon.src} />

        {isNoIndexed && <meta name="robots" content="noindex" />}
        <meta property="og:title" content={title} />
        <meta property="og:type" content="website" />
        {/* Image used here is the SEO Image in the SEO Metadata on Homepage */}
        <meta property="og:image" content={seoImageUrl} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="twitter:title" content={title} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@greenlightcard" />
        <meta name="twitter:image" content={seoImageUrl} />
        <meta name="twitter:url" content={url} />
        <meta name="twitter:description" content={description} />
        <meta
          name="google-site-verification"
          content="tRSY9AVtYMJtn1RC2sV52cdh4vXb7kdRpSO6vsumTWM"
        />
        <meta property="fb:app_id" content="1754995511484842" />
        <meta
          name="p:domain_verify"
          content="89139fcf35813cd089059de5e94a5ae2"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      {queryString && (
        <Script
          id="query-strings"
          type="text/javascript"
          data-script-order="0"
        >{`
            if (window && window.location && window.location.search === '') {
              history.replaceState({}, document.title, window.location.pathname + '?${queryString}')
            }
          `}</Script>
      )}
      {slug === 'faq' && (
        <Script
          id="faq"
          type="application/ld+json"
          strategy="afterInteractive"
        >{`${FAQRichResults}`}</Script>
      )}
      {schema && (
        <Script
          id="schema"
          type="application/ld+json"
          strategy="afterInteractive"
        >
          {schema?.fields?.schemaJson}
        </Script>
      )}
      {pathPrefix === '/blog' && (
        <Script
          id="blog-schema"
          type="application/ld+json"
          strategy="afterInteractive"
        >
          {JSON.stringify(
            DynamicSchemaJson(title, url, description, image, datePublished),
            null,
            '\t'
          )}
        </Script>
      )}
    </>
  );
}
